/* eslint-disable no-underscore-dangle */
import { ReactNode } from 'react';
import Swal from 'sweetalert2';

import { store } from 'redux/store';
import { translate } from 'hooks/useTranslate';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { deletePartner as deletePartnerRequest } from 'modules/partners/redux/partnerActions';
import {
  getPartnerDeletionPreview,
  PartnerDeletionPreview,
  PartnerDeletionPreviewReferences,
} from 'modules/partners/api/getPartnerDeletionPreview';

type PartnerEntityConfig = {
  key: keyof PartnerDeletionPreviewReferences;
  labelKey: string;
  icon: string;
};

type CleanupEntityConfig = {
  key: 'societyMembership' | 'userLinks';
  labelKey: string;
  icon: string;
};

type DeletePartnerParams = {
  data: { _id: string; name: string };
  society?: { _id?: string; name?: string };
};

const REFERENCE_CONFIG: PartnerEntityConfig[] = [
  { key: 'shares', labelKey: 'Shares', icon: 'ni-pie' },
  { key: 'operations', labelKey: 'Operations', icon: 'ni-tranx' },
  { key: 'operationRaws', labelKey: 'OperationRaws', icon: 'ni-file-docs' },
  { key: 'customMovements', labelKey: 'CustomMovements', icon: 'ni-shuffle' },
  { key: 'boards', labelKey: 'Boards', icon: 'ni-briefcase' },
  {
    key: 'partnerRepresentants',
    labelKey: 'PartnerRepresentants',
    icon: 'ni-user-circle',
  },
  { key: 'sharesTitles', labelKey: 'SharesTitles', icon: 'ni-file-img' },
  {
    key: 'sharesOwnershipCertificates',
    labelKey: 'SharesOwnershipCertificates',
    icon: 'ni-file-check',
  },
  { key: 'stockPlans', labelKey: 'StockPlans', icon: 'ni-gift' },
  { key: 'tradingOffers', labelKey: 'TradingOffers', icon: 'ni-bar-chart' },
  {
    key: 'tradingOfferOperations',
    labelKey: 'TradingOfferOperations',
    icon: 'ni-swap',
  },
  {
    key: 'tenderOfferShares',
    labelKey: 'TenderOfferShares',
    icon: 'ni-files',
  },
  { key: 'investors', labelKey: 'Investors', icon: 'ni-trend-up' },
  { key: 'notes', labelKey: 'Notes', icon: 'ni-note-add' },
  { key: 'observations', labelKey: 'Observations', icon: 'ni-eye' },
  { key: 'partnerDocuments', labelKey: 'PartnerDocuments', icon: 'ni-file-text' },
];

const CLEANUP_CONFIG: CleanupEntityConfig[] = [
  {
    key: 'societyMembership',
    labelKey: 'SocietyMembership',
    icon: 'ni-building',
  },
  { key: 'userLinks', labelKey: 'UserLinks', icon: 'ni-user' },
];

const renderReferenceRow = (
  { key, labelKey, icon }: PartnerEntityConfig,
  preview: PartnerDeletionPreview,
  blockedMode: boolean
): string => {
  const count = preview.references[key] ?? 0;
  const hasItems = count > 0;
  let badgeBg = '#8094ae';
  if (blockedMode && hasItems) {
    badgeBg = '#e14954';
  } else if (hasItems) {
    badgeBg = '#8094ae';
  }
  let rowOpacity = '0.55';
  if (blockedMode && hasItems) {
    rowOpacity = '1';
  } else if (hasItems) {
    rowOpacity = '0.85';
  }

  return `
    <div style="
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 5px 8px;
      border-radius: 6px;
      opacity: ${rowOpacity};
      background: ${blockedMode && hasItems ? 'rgba(225,73,84,0.04)' : 'transparent'};
    ">
      <div style="display: flex; align-items: center; gap: 8px;">
        <em class="icon ni ${icon}" style="font-size: 15px; color: ${
    blockedMode && hasItems ? '#526484' : '#8094ae'
  };"></em>
        <span style="font-size: 13px; color: #526484; white-space: nowrap;">${translate(
          labelKey
        )}</span>
      </div>
      <span style="
        background: ${badgeBg};
        color: #fff;
        border-radius: 20px;
        padding: 1px 9px;
        font-size: 12px;
        font-weight: 700;
        min-width: 28px;
        text-align: center;
      ">${count}</span>
    </div>`;
};

const renderCleanupRow = (
  { key, labelKey, icon }: CleanupEntityConfig,
  preview: PartnerDeletionPreview
): string => {
  const count = preview.cleanup[key] ?? 0;

  return `
    <div style="
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 5px 8px;
      border-radius: 6px;
      opacity: 0.85;
    ">
      <div style="display: flex; align-items: center; gap: 8px;">
        <em class="icon ni ${icon}" style="font-size: 15px; color: #8094ae;"></em>
        <span style="font-size: 13px; color: #526484; white-space: nowrap;">${translate(
          labelKey
        )}</span>
      </div>
      <span style="
        background: #8094ae;
        color: #fff;
        border-radius: 20px;
        padding: 1px 9px;
        font-size: 12px;
        font-weight: 700;
        min-width: 28px;
        text-align: center;
      ">${count}</span>
    </div>`;
};

const buildPreviewHtml = (
  preview: PartnerDeletionPreview,
  blockedMode: boolean
): string => {
  const mid = Math.ceil(REFERENCE_CONFIG.length / 2);
  const col1 = REFERENCE_CONFIG.slice(0, mid);
  const col2 = REFERENCE_CONFIG.slice(mid);

  const blockedDescription = blockedMode
    ? `<p style="font-size: 12px; color: #e14954; margin-bottom: 12px;">${translate(
        'PartnerDeleteBlockedDescription'
      )}</p>`
    : '';

  return `
    <div style="text-align: left; padding-bottom: 20px;">
      <p style="font-size: 13px; color: #526484; margin-bottom: 4px;">
        ${translate('Partner')}: <strong style="color: #364a63;">${
    preview.partnerName
  }</strong>
      </p>
      <p style="font-size: 12px; color: #8094ae; margin-bottom: 12px;">
        ${translate('PartnerDeletePreviewSubtitle')}
      </p>
      ${blockedDescription}

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 2px 16px; margin-bottom: 14px;">
        <div>${col1.map((item) => renderReferenceRow(item, preview, blockedMode)).join('')}</div>
        <div>${col2.map((item) => renderReferenceRow(item, preview, blockedMode)).join('')}</div>
      </div>

      <div style="
        border: 1.5px solid #8094ae;
        border-radius: 8px;
        padding: 10px 12px;
        background: #f5f6fa;
      ">
        <p style="font-size: 12px; color: #526484; font-weight: 600; margin: 0 0 8px;">
          ${translate('PartnerDeleteCleanupSection')}
        </p>
        ${CLEANUP_CONFIG.map((item) => renderCleanupRow(item, preview)).join('')}
      </div>
    </div>`;
};

export const deletePartner = async ({
  data,
}: DeletePartnerParams): Promise<void> => {
  store.dispatch(setModal((<CustomLoading />) as ReactNode));

  let preview: PartnerDeletionPreview | null = null;

  try {
    preview = await getPartnerDeletionPreview(data._id);
  } catch {
    store.dispatch(setModal(null));
    errorSwal(translate('ErrorLoadingPartnerDeletePreview'));
    return;
  }

  store.dispatch(setModal(null));

  if (!preview.canDelete) {
    Swal.fire({
      icon: 'error',
      title: `<h4 class="nk-modal-title">${translate(
        'PartnerDeleteBlockedTitle'
      )}</h4>`,
      html: buildPreviewHtml(preview, true),
      confirmButtonText: translate('OK'),
      confirmButtonColor: '#6576ff',
      allowOutsideClick: false,
      showCancelButton: false,
      width: 680,
      customClass: {
        htmlContainer: 'swal-delete-preview',
      },
    });
    return;
  }

  const CONFIRM_WORD = translate('ConfirmDeleteWord');

  return Swal.fire({
    icon: 'warning',
    title: `<h4 class="nk-modal-title">${translate(
      'AreYouSureAboutDeletePartner'
    )}</h4>`,
    html: buildPreviewHtml(preview, false),
    input: 'text',
    inputPlaceholder: translate('TypeToConfirm', {
      confirmationText: CONFIRM_WORD,
    }),
    inputAttributes: { autocomplete: 'off' },
    confirmButtonText: translate('OkDelete'),
    confirmButtonColor: '#e14954',
    allowOutsideClick: false,
    showCancelButton: true,
    cancelButtonText: translate('Cancel'),
    width: 680,
    customClass: {
      htmlContainer: 'swal-delete-preview',
      input: 'swal-delete-confirm-input',
    },
    didOpen: () => {
      const confirmBtn = Swal.getConfirmButton();
      const input = Swal.getInput();
      if (!confirmBtn || !input) return;

      confirmBtn.disabled = true;
      input.addEventListener('input', () => {
        confirmBtn.disabled = input.value !== CONFIRM_WORD;
      });
    },
    preConfirm: (value: string) => {
      if (value !== CONFIRM_WORD) {
        Swal.showValidationMessage(
          translate('MustTypeExactly', { word: CONFIRM_WORD })
        );
        return false;
      }
      return true;
    },
  }).then(async (result) => {
    if (!result.isConfirmed) return;

    store.dispatch(setModal((<CustomLoading />) as ReactNode));

    try {
      await (store.dispatch as any)(deletePartnerRequest(data._id));
      successSwal(translate('PartnerSuccessfullyRemoved'));
    } catch {
      errorSwal(translate('ErrorDeletingPartner'));
    } finally {
      store.dispatch(setModal(null));
    }
  });
};
